import React, { useRef, useState } from 'react'
import {
  PlusIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  PhotoIcon,
  ListBulletIcon,
  ExclamationTriangleIcon,
  Bars3BottomLeftIcon,
} from '@heroicons/react/24/outline'
import HelpBlockRenderer from './HelpBlockRenderer'
import { BoldText } from './helpText'

const INSERT_OPTIONS = [
  { type: 'paragraph', label: 'Text', icon: Bars3BottomLeftIcon },
  { type: 'heading', label: 'Heading', icon: Bars3BottomLeftIcon },
  { type: 'bullets', label: 'Bullets', icon: ListBulletIcon },
  { type: 'note', label: 'Callout', icon: ExclamationTriangleIcon },
  { type: 'image', label: 'Image', icon: PhotoIcon },
]

export function emptyBlock(type) {
  switch (type) {
    case 'heading':
      return { type: 'heading', level: 3, text: '' }
    case 'bullets':
      return { type: 'bullets', items: [''] }
    case 'note':
      return { type: 'note', variant: 'info', text: '' }
    case 'image':
      return { type: 'image', asset_id: '', alt: '', caption: '' }
    case 'legal_footer':
      return { type: 'legal_footer', text: '' }
    default:
      return { type: 'paragraph', text: '' }
  }
}

function InsertBlockMenu({ onPick }) {
  const [open, setOpen] = useState(false)

  return (
    <div className="relative flex justify-center h-3 group my-0.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex h-5 w-5 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400 text-xs opacity-60 sm:opacity-0 sm:group-hover:opacity-100 hover:border-psg-teal hover:text-psg-teal transition-all"
        title="Add block"
      >
        <PlusIcon className="w-3 h-3" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} aria-hidden />
          <div className="absolute top-full left-1/2 -translate-x-1/2 z-30 mt-0.5 min-w-[9rem] rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
            {INSERT_OPTIONS.map((opt) => {
              const Icon = opt.icon
              return (
                <button
                  key={opt.type}
                  type="button"
                  onClick={() => {
                    onPick(opt.type)
                    setOpen(false)
                  }}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                >
                  <Icon className="w-3.5 h-3.5 text-slate-400" />
                  {opt.label}
                </button>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

const fieldBase =
  'w-full bg-transparent border-0 focus:ring-0 outline-none resize-none leading-snug placeholder:text-slate-300'

/** Click rendered text to edit; blur shows **bold** as formatted. */
function MarkdownField({
  value,
  onChange,
  onKeyDown,
  multiline = false,
  placeholder,
  displayClassName = 'text-sm text-slate-600 leading-snug',
  editClassName = 'text-sm text-slate-600',
  rows = 1,
}) {
  const [focused, setFocused] = useState(false)
  const ref = useRef(null)

  const startEdit = () => {
    setFocused(true)
    requestAnimationFrame(() => ref.current?.focus())
  }

  if (!focused) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={startEdit}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            startEdit()
          }
        }}
        className={`cursor-text min-h-[1.25rem] ${displayClassName} ${!value ? 'text-slate-300' : ''}`}
      >
        {value ? <BoldText text={value} /> : placeholder}
      </div>
    )
  }

  if (multiline) {
    return (
      <textarea
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setFocused(false)}
        onKeyDown={onKeyDown}
        rows={rows}
        placeholder={placeholder}
        className={`${fieldBase} ${editClassName} border-b border-slate-200 py-0.5`}
      />
    )
  }

  return (
    <input
      ref={ref}
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={() => setFocused(false)}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      className={`${fieldBase} ${editClassName} border-b border-slate-200 py-0.5`}
    />
  )
}

function BlockChrome({ index, total, onMove, onRemove, children }) {
  return (
    <div className="group/block relative -mx-1 px-1 py-0.5 hover:bg-slate-50/60 rounded transition-colors">
      <div className="absolute -right-0.5 top-0 flex items-center gap-0 opacity-0 group-hover/block:opacity-100 transition-opacity">
        <button type="button" onClick={() => onMove(-1)} disabled={index === 0} className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-30" title="Move up">
          <ChevronUpIcon className="w-3 h-3" />
        </button>
        <button type="button" onClick={() => onMove(1)} disabled={index >= total - 1} className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-30" title="Move down">
          <ChevronDownIcon className="w-3 h-3" />
        </button>
        <button type="button" onClick={onRemove} className="p-0.5 text-red-400 hover:text-red-600" title="Remove">
          <TrashIcon className="w-3 h-3" />
        </button>
      </div>
      {children}
    </div>
  )
}

function EditableBlock({ block, index, total, onChange, onMove, onRemove, onUploadImage, uploadBusy }) {
  const fileRef = useRef(null)
  const update = (patch) => onChange({ ...block, ...patch })

  if (block.type === 'legal_footer') {
    return (
      <div className="mt-3 pt-3 border-t border-slate-200">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Legal footer</p>
        <div className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5">
          <MarkdownField
            value={block.text || ''}
            onChange={(text) => update({ text })}
            multiline
            rows={2}
            placeholder="Tipping-off notice…"
            displayClassName="text-xs text-slate-600 leading-snug"
            editClassName="text-xs text-slate-600"
          />
        </div>
      </div>
    )
  }

  return (
    <BlockChrome index={index} total={total} onMove={onMove} onRemove={onRemove}>
      {block.type === 'paragraph' && (
        <MarkdownField
          value={block.text || ''}
          onChange={(text) => update({ text })}
          multiline
          rows={Math.max(1, (block.text || '').split('\n').length)}
          placeholder="Write something…"
          displayClassName="text-sm text-slate-600 leading-snug whitespace-pre-line"
          editClassName="text-sm text-slate-600"
        />
      )}

      {block.type === 'heading' && (
        <div>
          <select
            value={block.level || 3}
            onChange={(e) => update({ level: Number(e.target.value) })}
            className="text-[10px] text-slate-400 border-0 bg-transparent p-0 mb-0.5 focus:ring-0 cursor-pointer"
          >
            <option value={2}>Large heading</option>
            <option value={3}>Heading</option>
            <option value={4}>Small heading</option>
          </select>
          <MarkdownField
            value={block.text || ''}
            onChange={(text) => update({ text })}
            placeholder="Heading"
            displayClassName="text-base font-bold text-slate-900 leading-tight"
            editClassName="text-base font-bold text-slate-900"
          />
        </div>
      )}

      {block.type === 'bullets' && (
        <ul className="list-disc pl-5 space-y-0.5 text-sm text-slate-600 leading-snug marker:text-slate-400">
          {(block.items || ['']).map((item, bi) => (
            <li key={bi} className="pl-0.5">
              <MarkdownField
                value={item}
                onChange={(text) => {
                  const items = [...(block.items || [])]
                  items[bi] = text
                  update({ items })
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    const items = [...(block.items || [])]
                    items.splice(bi + 1, 0, '')
                    update({ items })
                  }
                  if (e.key === 'Backspace' && !item && (block.items || []).length > 1) {
                    e.preventDefault()
                    update({ items: (block.items || []).filter((_, i) => i !== bi) })
                  }
                }}
                placeholder="List item"
                displayClassName="text-sm text-slate-600 leading-snug"
                editClassName="text-sm text-slate-600"
              />
            </li>
          ))}
          <li className="list-none -ml-5 mt-0.5">
            <button
              type="button"
              onClick={() => update({ items: [...(block.items || []), ''] })}
              className="text-[11px] text-psg-teal hover:underline"
            >
              + add bullet
            </button>
          </li>
        </ul>
      )}

      {block.type === 'note' && (
        <div className={`rounded-md border px-2.5 py-1.5 ${block.variant === 'warning' ? 'border-amber-200 bg-amber-50' : 'border-sky-200 bg-sky-50'}`}>
          <select
            value={block.variant || 'info'}
            onChange={(e) => update({ variant: e.target.value })}
            className="text-[10px] mb-0.5 border-0 bg-transparent text-slate-500 p-0 focus:ring-0"
          >
            <option value="info">Info</option>
            <option value="warning">Warning</option>
          </select>
          <MarkdownField
            value={block.text || ''}
            onChange={(text) => update({ text })}
            multiline
            rows={2}
            placeholder="Callout…"
            displayClassName="text-sm text-slate-700 leading-snug"
            editClassName="text-sm text-slate-700"
          />
        </div>
      )}

      {block.type === 'image' && (
        <div className="space-y-1.5">
          <HelpBlockRenderer blocks={[block]} />
          <div className="flex flex-wrap gap-2 text-xs">
            <button
              type="button"
              disabled={uploadBusy}
              onClick={() => fileRef.current?.click()}
              className="px-2 py-0.5 rounded border border-slate-200 bg-white hover:bg-slate-50 text-slate-600"
            >
              {uploadBusy ? 'Uploading…' : block.asset_id ? 'Replace' : 'Upload'}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0]
                e.target.value = ''
                if (!file || !onUploadImage) return
                const assetId = await onUploadImage(file)
                if (assetId) update({ asset_id: assetId })
              }}
            />
            <input
              type="text"
              value={block.caption || ''}
              onChange={(e) => update({ caption: e.target.value })}
              placeholder="Caption"
              className="flex-1 min-w-[6rem] px-2 py-0.5 border border-slate-200 rounded text-slate-600"
            />
          </div>
        </div>
      )}
    </BlockChrome>
  )
}

export default function HelpWysiwygEditor({
  blocks = [],
  onChange,
  onUploadImage,
  uploadBusy,
  showLegalFooter = false,
}) {
  const bodyBlocks = showLegalFooter
    ? blocks.filter((b) => b.type !== 'legal_footer')
    : blocks
  const footerBlock = blocks.find((b) => b.type === 'legal_footer') || emptyBlock('legal_footer')

  const setBlocks = (nextBody, footer = footerBlock) => {
    if (showLegalFooter) {
      onChange([...nextBody, footer])
    } else {
      onChange(nextBody)
    }
  }

  const insertAt = (index, type) => {
    const next = [...bodyBlocks]
    next.splice(index, 0, emptyBlock(type))
    setBlocks(next)
  }

  const updateAt = (index, block) => {
    setBlocks(bodyBlocks.map((b, i) => (i === index ? block : b)))
  }

  const removeAt = (index) => {
    setBlocks(bodyBlocks.filter((_, i) => i !== index))
  }

  const moveAt = (index, dir) => {
    const next = [...bodyBlocks]
    const j = index + dir
    if (j < 0 || j >= next.length) return
    ;[next[index], next[j]] = [next[j], next[index]]
    setBlocks(next)
  }

  return (
    <div className="help-wysiwyg-canvas space-y-0">
      {bodyBlocks.length === 0 && (
        <>
          <p className="text-center text-xs text-slate-400 py-3">Click + to add content</p>
          <InsertBlockMenu onPick={(type) => insertAt(0, type)} />
        </>
      )}

      {bodyBlocks.map((block, index) => (
        <React.Fragment key={index}>
          <EditableBlock
            block={block}
            index={index}
            total={bodyBlocks.length}
            onChange={(b) => updateAt(index, b)}
            onMove={(dir) => moveAt(index, dir)}
            onRemove={() => removeAt(index)}
            onUploadImage={onUploadImage}
            uploadBusy={uploadBusy}
          />
          <InsertBlockMenu onPick={(type) => insertAt(index + 1, type)} />
        </React.Fragment>
      ))}

      {showLegalFooter && (
        <EditableBlock
          block={footerBlock}
          index={0}
          total={1}
          onChange={(b) => setBlocks(bodyBlocks, b)}
          onMove={() => {}}
          onRemove={() => {}}
          onUploadImage={onUploadImage}
          uploadBusy={uploadBusy}
        />
      )}
    </div>
  )
}
